// src/components/ForgotPassword.tsx
import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../utils/axiosConfig';
import { validateEmail } from '../utils/validation';
import { getCaptchaToken } from '../utils/captcha';

import '../styles/PasswordFlows.css';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Initialize CSRF token before the form POST
  useEffect(() => {
    axiosInstance.get('/health').catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    const emailCheck = validateEmail(email);
    if (!emailCheck.valid) {
      setError(emailCheck.message || 'Enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      // Phase 20.1 — no-op until REACT_APP_RECAPTCHA_SITE_KEY is configured
      const captchaToken = await getCaptchaToken('forgot_password');
      await axiosInstance.post('/auth/forgot-password', {
        email: email.trim(),
        ...(captchaToken ? { captchaToken } : {}),
      });
      setMessage('If that email is registered, you’ll receive a reset link shortly.');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="hb-password-container hb-auth-split">
      {/* Welcoming side panel (visual only — form logic unchanged) */}
      <aside className="hb-auth-aside">
        <div className="hb-auth-orbs" aria-hidden="true">
          <span /><span /><span />
        </div>
        <div className="hb-auth-aside-inner">
          <div className="hb-auth-brand">
            <span className="hb-auth-brand-mark">S</span> Sumbitrbuddy
          </div>
          <h2 className="hb-auth-headline">
            Let's get you <span className="hb-auth-accent">back in</span>.
          </h2>
          <p className="hb-auth-lead">
            Reset your password in a minute and pick your job hunt right back up
            where you left it.
          </p>
          <ul className="hb-auth-points">
            <li>Secure, encrypted reset link</li>
            <li>Your data stays private</li>
            <li>Back to your board in seconds</li>
          </ul>
          <div className="hb-auth-chips">
            <span className="hb-auth-chip">Encrypted</span>
            <span className="hb-auth-chip hb-auth-chip-b">Secure reset</span>
          </div>
        </div>
      </aside>

      <div className="hb-auth-main">
      {/* Coach Message */}
      <div className="hb-coach-message">
        <span className="hb-coach-icon">🔐</span>
        <p>We'll help you get back into your account!</p>
      </div>

      <div className="hb-password-card">
        <h1 className="hb-password-title">Forgot Password?</h1>
        <p className="hb-password-subtitle">No worries. Enter your email and we'll send you a reset link.</p>

        {message && (
          <div className="hb-alert hb-alert-success">
            <span>✅</span>
            <p>{message}</p>
          </div>
        )}

        {error && (
          <div className="hb-alert hb-alert-error">
            <span>⚠️</span>
            <p>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="hb-password-form">
          <div className="hb-form-group">
            <label htmlFor="email" className="hb-label">Email Address</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              className="hb-input"
              placeholder="you@example.com"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className={loading ? 'hb-btn hb-btn-primary hb-btn-loading' : 'hb-btn hb-btn-primary'}
          >
            {loading ? '⏳ Sending...' : '📧 Send Reset Link'}
          </button>
        </form>

        <div className="hb-password-footer">
          <a href="/login" className="hb-link">
            ← Back to Login
          </a>
        </div>
      </div>
      </div>
    </div>
  );
};

export default ForgotPassword;